import {
  ApiConversion,
  ApiConversionType,
  ApiProject,
  ApiLocation,
  ApiConversionStateTrigger,
  ApiConversionState,
  ApiReservedConversion,
  ApiConversionStateLookupType,
} from "@incendium/api";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  SelectValidator,
  TextValidator,
  ValidatorForm,
} from "react-material-ui-form-validator";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { TriggerList } from "Components/TriggerList";
import {
  Box,
  CircularProgress,
  Grid,
  Switch,
  Typography,
  SelectChangeEvent,
  InputAdornment,
  IconButton,
  Stack,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useNotification } from "Hooks";
import makeStyles from "@mui/styles/makeStyles";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useClientIdFromUrl } from "Hooks";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import produce from "immer";
import { StyledButton, TextFieldHeading } from "Components/TagPages";
import { enumToArray, formatEnumVal } from "Helpers/enumToText";
import { ReservedConversionSelector } from "./ReservedConversionSelector";
import { preventSubmitTooltipText } from "Components/FormHandlersForm";
import {
  AccordianChartBuilderSidebarBlock,
  ChartBuilderSidebarContainer,
} from "features/chartLibrary";
import { deleteConversionState, saveConversionState } from "features/location";
import Meta_Ads_logo from "Assets/Images/ThirdParty/Meta_lockup_positive primary_RGB.svg";
import Image from "mui-image";
import { IConversionPayload } from "../types";
import { useUpdateEffect } from "react-use";
import { useIframeContext } from "features/smartIFrame/providers/OldIframeProvider";
import { useSmartIframeContext } from "features/smartIFrame/providers/SmartIFrameProvider";
import {
  debugHighlight,
  debugHighlightCollection,
  debugHighlightFromText,
  debugRepeated,
  debugSwitchLookup,
  debugTags,
  debugUnique,
  THighlight,
} from "@incendium/inc-ts-helpers";
import { useConversions } from "Hooks/useConversions";

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 120,
    width: "100%",
  },
  selectEmpty: {
    marginTop: theme.spacing(4),
  },
}));

interface triggerWithTempId extends ApiConversionState {
  tempId?: number;
}

const getDebugTargetsForTrigger = (
  trigger: ApiConversionStateTrigger,
): string[] => {
  switch (trigger) {
    case ApiConversionStateTrigger.CLICK:
      return ["a", "button"];
    case ApiConversionStateTrigger.SUBMIT:
      return ["form"];
    case ApiConversionStateTrigger.TIME_ON_ELEMENT:
      return ["*"];
    default:
      return [];
  }
};

const TRIGGER_OVERVIEW_COLOUR = "#9ec5eb";

const buildTriggerHighlights = (
  triggerStates: triggerWithTempId[],
  colour: string,
): THighlight[] => {
  const highlights: THighlight[] = [];

  for (const triggerState of triggerStates) {
    if (triggerState.targetInnerText && triggerState.targetInnerText !== "") {
      highlights.push({
        el: "",
        text: triggerState.targetInnerText,
        lookup: triggerState.lookup,
        colour,
      });
      continue;
    }

    if (triggerState.target && triggerState.target !== "") {
      highlights.push({
        el: triggerState.target,
        repeated: triggerState.repeatedSelector,
        colour,
      });
    }
  }

  return highlights;
};

const ReservedTipText = (
  <>
    <p>
      <strong>Reserved conversions</strong> are a special type of conversion
      that are used in particular reports in an analyse
    </p>
    <p>
      <strong>Add To Basket</strong> - this is used in product reports and
      should be used for the action that add's a item to your cart for ecom
    </p>
  </>
);

const MicroMacroToolTipText = (
  <>
    <p>
      <strong>Conversions:</strong> core conversions which are critical
      performance measures, e.g. contact us form submission, or a sale on an
      ecom site.
      <br />
      Important: We only track 1 Conversion per pageview
    </p>
    <p>
      <strong>Interactions: </strong> smaller conversion metrices, e.g. tracking
      button clicks or downloads.
      <br />
      Important: We can track multiple Interactions per pageview but if the same
      conversion happens within a second of each other we count these together
    </p>
  </>
);

const TriggerCommaValue = ({
  loading,
  initialValue,
  handleChange,
}: {
  loading: boolean;
  initialValue: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>, v: string) => void;
}) => {
  const [values, setValues] = useState(
    initialValue && initialValue !== "" ? initialValue.split(",") : [""],
  );

  useEffect(() => {
    const v = values
      .filter((a) => a !== "")
      .join(",")
      .trim();

    handleChange({} as any, v);
  }, [values, handleChange]);

  const onChange = useCallback(
    (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues((oldValues) =>
        produce(oldValues, (draft) => {
          draft[i] = e.target.value;
        }),
      );
    },
    [],
  );

  return (
    <>
      {values.map((value, i) => (
        <Box key={i} mb={1}>
          <TextValidator
            value={value}
            variant="outlined"
            size="small"
            name="selector"
            fullWidth
            disabled={loading}
            validators={["required"]}
            errorMessages={["selector is required"]}
            onChange={onChange(i)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {values.length > 1 && (
                    <IconButton
                      onClick={() =>
                        setValues(
                          produce(values, (draft) => {
                            draft.splice(i, 1);
                          }),
                        )
                      }
                      edge="end"
                    >
                      <Remove />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => setValues([...values, ""])}
                    edge="end"
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      ))}
    </>
  );
};

export const ConversionForm = ({
  project,
  location,
  conversion,
  onCancel,
  onAdd,
  onEdit,
  isMobile,
  initialConversionType,
  onAttach,
  attachedConversionIds,
}: {
  project: ApiProject;
  location: ApiLocation;
  conversion?: ApiConversion;
  onCancel: () => void;
  onEdit?: (conversion: ApiConversion, triggers: ApiConversionState[]) => void;
  onAdd?: (conversion: ApiConversion, triggers: ApiConversionState[]) => void;
  isMobile: boolean;
  initialConversionType: ApiConversionType;
  onAttach?: (
    conversion: ApiConversion,
    triggers: ApiConversionState[],
  ) => void;
  attachedConversionIds?: number[];
}) => {
  const classes = useStyles();
  const clientId = useClientIdFromUrl();
  const [state, setState] = useState<IConversionPayload>({
    name: (conversion?.name as string) || "",
    selector: "",
    targetText: "",
    reserved: conversion?.reserved || ApiReservedConversion.NOT_RESERVED,
    value: null,
    pushToMeta: conversion ? Boolean(conversion.pushToMeta) : false,
    lookup: ApiConversionStateLookupType.EQUALS,
  });

  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [type, setType] = useState(
    conversion && conversion.type
      ? conversion.type
      : initialConversionType !== ApiConversionType.UNSPECIFIED
        ? initialConversionType
        : ApiConversionType.MICRO,
  );
  const [useReserved, setUseReserved] = useState(
    conversion && conversion.reserved !== ApiReservedConversion.NOT_RESERVED,
  );
  const [selectedTag, setSelectedTag] = useState<
    string | null | triggerWithTempId
  >(null);
  const [trigger, setTrigger] = useState(ApiConversionStateTrigger.CLICK);
  const [newTriggers, setNewTriggers] = useState<triggerWithTempId[]>([]);
  const [triggers, setTriggers] = useState<ApiConversionState[]>([]);
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [selectorRepeated, setSelectorRepeated] = useState(false);
  const [trackOffline, setTrackOffline] = useState<boolean | null>(false);
  const [preventSubmit, setPreventSubmit] = useState<boolean>(false);
  const [offlineFieldName, setOfflineFieldName] = useState<string | null>(null);
  const [useText, setUseText] = useState(false);
  const { selectedSelector, selectedText, clearIframeSelection } =
    useIframeContext();
  const { sendMessage, iframeReadyVersion } = useSmartIframeContext();
  const [didManualEdit, setDidManualEdit] = useState(false);
  const [useExistingConversion, setUseExistingConversion] = useState(false);
  const [existingConversionId, setExistingConversionId] = useState<
    number | null
  >(null);
  const {
    conversions,
    refetch: refetchProjectConversions,
    fetched: conversionsFetched,
  } = useConversions();

  const attachedIds = useMemo(
    () =>
      (attachedConversionIds || []).filter(
        (id): id is number => typeof id === "number",
      ),
    [attachedConversionIds],
  );
  const availableExistingConversions = useMemo(
    () =>
      conversions.filter(
        (conv) => typeof conv.id === "number" && !attachedIds.includes(conv.id),
      ),
    [conversions, attachedIds],
  );
  const sortedExistingConversions = useMemo(
    () =>
      [...availableExistingConversions].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      ),
    [availableExistingConversions],
  );
  const canAttachExisting = !conversion;
  const hasAttachOptions = availableExistingConversions.length > 0;
  const selectedExistingConversion = useMemo(
    () =>
      existingConversionId != null
        ? conversions.find(
            (conv) =>
              typeof conv.id === "number" && conv.id === existingConversionId,
          )
        : null,
    [existingConversionId, conversions],
  );

  useEffect(() => {
    if (conversion && conversion.states) {
      setTriggers(conversion.states);
    }
  }, [conversion]);

  useEffect(() => {
    if (conversion) {
      setUseExistingConversion(false);
    }
  }, [conversion]);

  useEffect(() => {
    if (!useExistingConversion) {
      setExistingConversionId(null);
      return;
    }
    if (!conversionsFetched) {
      refetchProjectConversions();
    }
  }, [useExistingConversion, conversionsFetched, refetchProjectConversions]);

  useEffect(() => {
    if (useExistingConversion) {
      setTriggers(selectedExistingConversion?.states || []);
      setNewTriggers([]);
      setSelectedTag(null);
    }
  }, [useExistingConversion, selectedExistingConversion]);

  useEffect(() => {
    if (
      useExistingConversion &&
      existingConversionId != null &&
      !availableExistingConversions.some(
        (conv) => conv.id === existingConversionId,
      )
    ) {
      setExistingConversionId(null);
    }
  }, [
    useExistingConversion,
    existingConversionId,
    availableExistingConversions,
  ]);

  const shouldShowTriggers =
    !useExistingConversion || !!selectedExistingConversion;

  useEffect(() => {
    if (!selectedTag) {
      return;
    }
    if (selectedSelector && !useText && !didManualEdit) {
      setState((old) => ({
        ...old,
        selector: selectedSelector,
      }));
    }
    if (selectedText && useText) {
      setState((old) => ({
        ...old,
        targetText: selectedText,
      }));
    }
  }, [selectedSelector, selectedText, useText, didManualEdit, selectedTag]);

  useEffect(() => {
    if (!selectedTag) {
      sendMessage({
        type: debugTags,
        data: [],
      });
      sendMessage({
        type: debugHighlightCollection,
        data: buildTriggerHighlights(
          [...triggers, ...newTriggers],
          TRIGGER_OVERVIEW_COLOUR,
        ),
      });
      return;
    }

    sendMessage({
      type: debugHighlight,
      data: "",
    });
    sendMessage({
      type: debugHighlightFromText,
      data: "",
    });
    sendMessage({
      type: debugTags,
      data: getDebugTargetsForTrigger(trigger),
    });
  }, [
    trigger,
    selectedTag,
    triggers,
    newTriggers,
    sendMessage,
    iframeReadyVersion,
  ]);

  useEffect(() => {
    if (!selectedTag) {
      clearIframeSelection();
      sendMessage({ type: "clearSelection" } as any);
      setDidManualEdit(false);
      setUseText(false);
      setTrigger(ApiConversionStateTrigger.CLICK);
      setTrackOffline(false);
      setOfflineFieldName(null);
      setPreventSubmit(false);
      setState(
        produce(state, (draft) => {
          draft.selector = "";
          draft.targetText = "";
          draft.lookup = ApiConversionStateLookupType.EQUALS;
          draft.value = null;
        }),
      );
      setSelectorRepeated(false);
      return;
    }

    if (selectedTag === "new") {
      clearIframeSelection();
      sendMessage({ type: "clearSelection" } as any);
      setDidManualEdit(false);
      setUseText(false);
      setState((old) => ({ ...old, selector: "", targetText: "" }));
      return;
    }

    setState((old) => ({ ...old, selector: "" }));
    if (typeof selectedTag !== "string") {
      selectedTag.trigger && setTrigger(selectedTag.trigger);
      selectedTag.offlineFieldName &&
        setOfflineFieldName(selectedTag.offlineFieldName);
      selectedTag.offline && setTrackOffline(selectedTag.offline);
      selectedTag.preventSubmit && setPreventSubmit(selectedTag.preventSubmit);
      selectedTag.repeatedSelector &&
        setSelectorRepeated(selectedTag.repeatedSelector);

      selectedTag.targetInnerText &&
        selectedTag.targetInnerText !== "" &&
        setUseText(true);

      setState(
        produce(state, (draft) => {
          draft.selector = selectedTag.target || "";
          draft.targetText = selectedTag.targetInnerText || "";
          draft.value = selectedTag.value || null;
          draft.lookup =
            selectedTag.lookup || ApiConversionStateLookupType.EQUALS;
        }),
      );
    }
  }, [selectedTag, clearIframeSelection, sendMessage]);

  useEffect(() => {
    setIsReady(true);
    return () => {
      sendMessage({
        type: debugTags,
        data: [],
      });
      sendMessage({
        type: debugHighlightCollection,
        data: [],
      });
    };
  }, [sendMessage]);

  useEffect(() => {
    if (!selectedTag) {
      return;
    }
    if (useText) {
      sendMessage({
        type: debugSwitchLookup,
        data: state.lookup,
      });
      sendMessage({
        type: debugHighlightFromText,
        data: state.targetText,
      });
    } else {
      sendMessage({
        type: debugSwitchLookup,
        data: ApiConversionStateLookupType.EQUALS,
      });
    }
  }, [
    state.lookup,
    sendMessage,
    useText,
    state.targetText,
    iframeReadyVersion,
    selectedTag,
  ]);

  useEffect(() => {
    if (!selectedTag || useText) {
      return;
    }

    // Send repeated/unique mode first, then highlight
    sendMessage({
      type: selectorRepeated ? debugRepeated : debugUnique,
    });

    // Re-highlight with the updated repeated mode
    if (state.selector) {
      sendMessage({
        type: debugHighlight,
        data: state.selector,
      });
    }
  }, [
    state.selector,
    useText,
    selectorRepeated,
    sendMessage,
    iframeReadyVersion,
    selectedTag,
  ]);

  const handleTrackChange = () => setTrackOffline((state) => !state);

  const handleSelectChange = (event: any) => {
    const name = event.target.name as keyof typeof state;
    setState({
      ...state,
      [name]: event?.target?.value,
    });
  };

  const handleTriggerTypeChange = (event: SelectChangeEvent) => {
    setState((state) => ({ ...state, selector: "", value: null }));
    setTrigger(event.target.value as ApiConversionStateTrigger);
  };

  const handleSubmit = () => {
    setLoading(true);

    const newState: ApiConversion = {
      name: state.name,
      type,
      reserved: state.reserved as ApiReservedConversion,
      pushToMeta: state.pushToMeta,
    };

    const triggersToSave = newTriggers.map((t) => {
      const { tempId, ...rest } = t;
      return rest as ApiConversionState;
    });

    if (!conversion && useExistingConversion) {
      if (!selectedExistingConversion) {
        showErrorNotification("Please select a conversion to attach");
        setLoading(false);
        return;
      }
      onAttach && onAttach(selectedExistingConversion, triggersToSave);
      setNewTriggers([]);
      setLoading(false);
      return;
    }

    if (conversion) {
      onEdit &&
        onEdit(
          {
            ...newState,
            id: conversion.id,
          },
          triggersToSave,
        );
    } else {
      onAdd && onAdd(newState, triggersToSave);
    }
    setLoading(false);
  };

  const handleChange = useCallback(
    (key: keyof IConversionPayload) =>
      (e: React.ChangeEvent<HTMLInputElement>, value?: string | boolean) => {
        let val = typeof value !== "undefined" ? value : e.target.value;
        if (key === "selector") {
          setDidManualEdit(true);
          const str = String(val || "").trim();
          if (str === "") {
            sendMessage({ type: "clearSelection" } as any);
            // Allow next iframe selection to repopulate
            setDidManualEdit(false);
          } else {
            sendMessage({ type: debugHighlight, data: str } as any);
          }
        }
        setState((old) =>
          produce(old, (draft) => {
            draft[key as string] = val;
          }),
        );
      },
    [sendMessage],
  );

  const handleTypeChange = () =>
    setType((state) =>
      state === ApiConversionType.MACRO
        ? ApiConversionType.MICRO
        : ApiConversionType.MACRO,
    );
  const handleOfflineFieldNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => setOfflineFieldName(e.target.value);

  const handleTriggerDelete = async (conversionState?: triggerWithTempId) => {
    if (conversionState?.conversionId) {
      try {
        await deleteConversionState(
          Number(clientId),
          project.id!,
          location.id!,
          conversionState.conversionId!,
          conversionState.id!,
        );
        setTriggers((prevStates) =>
          produce(prevStates, (draft) => {
            const idx = draft.findIndex((d) => d.id === conversionState.id);
            if (idx >= 0) {
              draft.splice(idx, 1);
            }
          }),
        );
        showSuccessNotification("Trigger deleted");
      } catch (err) {
        showErrorNotification("Failed to deleted trigger");
      }
    } else {
      setNewTriggers((state) =>
        state.filter((trigger) => trigger.tempId !== conversionState?.tempId),
      );
      showSuccessNotification("Trigger deleted");
    }
  };

  const handleSubmitTrigger = async () => {
    const triggerState: triggerWithTempId = {
      tempId: Math.random() * 100,
      trigger: trigger,
      target: useText ? "" : state.selector,
      value: state.value || undefined,
      offline: trackOffline || false,
      offlineFieldName: offlineFieldName || "",
      repeatedSelector: selectorRepeated || false,
      preventSubmit: preventSubmit,
      targetInnerText: useText ? state.targetText : "",
      lookup: state.lookup,
    };

    if (selectedTag && typeof selectedTag !== "string") {
      if (selectedTag.tempId) {
        setNewTriggers((state) =>
          state.map((trigger) =>
            trigger.tempId === selectedTag.tempId ? triggerState : trigger,
          ),
        );
        showSuccessNotification("Trigger updated");
      } else {
        // standard update
        const res = await saveConversionState(
          Number(clientId),
          project.id!,
          location.id!,
          selectedTag?.conversionId!,
          {
            id: selectedTag?.id as number,
            trigger: trigger,
            target: useText ? "" : state.selector,
            value: triggerState.value,
            offlineFieldName: triggerState.offlineFieldName,
            offline: triggerState.offline,
            repeatedSelector: triggerState.repeatedSelector,
            preventSubmit: triggerState.preventSubmit,
            targetInnerText: useText ? state.targetText : "",
            lookup: state.lookup,
          },
        );
        showSuccessNotification("Trigger updated");
        setTriggers((state) =>
          state.map((trigger) =>
            trigger.id === selectedTag?.id
              ? {
                  ...res?.result,
                }
              : trigger,
          ),
        );
      }
    } else {
      setNewTriggers([...newTriggers, triggerState]);
      showSuccessNotification("Trigger added");
    }
    setSelectedTag(null);
  };

  const toggleTriggerText = useCallback((checked) => {
    setState((state) =>
      produce(state, (draft) => {
        if (!checked) {
          draft.targetText = "";
        } else {
          draft.selector = "";
        }
      }),
    );

    setUseText(checked);
  }, []);

  useUpdateEffect(() => {
    if (
      useReserved &&
      state.reserved &&
      state.reserved !== ApiReservedConversion.NOT_RESERVED
    ) {
      setState((state) =>
        produce(state, (draft) => {
          draft.name = formatEnumVal(state.reserved as ApiReservedConversion);
        }),
      );
    }
  }, [useReserved, state.reserved]);

  if (!isReady) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <CircularProgress size={"2rem"} />
      </Box>
    );
  }

  return (
    <ChartBuilderSidebarContainer>
      <ValidatorForm onSubmit={handleSubmit}>
        <AccordianChartBuilderSidebarBlock
          title="Basic Settings"
          subTitle="Configure conversion name and type"
        >
          <Stack spacing={3}>
            {canAttachExisting && (
              <StyledFlexBox>
                <Box display="flex" alignItems="center">
                  <Switch
                    // disabled={
                    //   loading ||
                    //   (!useExistingConversion && conversionsFetched && !hasAttachOptions)
                    // }
                    checked={useExistingConversion}
                    onChange={() => setUseExistingConversion((prev) => !prev)}
                  />
                  <Typography>Attach Existing Conversion</Typography>
                </Box>
                <ClickableTooltip
                  text="Reuse a conversion already configured for this project."
                  icon={<StyledHelp />}
                />
              </StyledFlexBox>
            )}

            {useExistingConversion && canAttachExisting ? (
              <Stack spacing={2}>
                {!conversionsFetched && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={"1rem"} />
                    <Typography variant="body2">
                      Loading conversions…
                    </Typography>
                  </Box>
                )}
                <Autocomplete
                  fullWidth
                  size="medium"
                  options={sortedExistingConversions}
                  value={selectedExistingConversion || null}
                  disabled={
                    loading || (!hasAttachOptions && conversionsFetched)
                  }
                  onChange={(_, value) =>
                    setExistingConversionId(value ? (value.id as number) : null)
                  }
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderOption={(props, option) => {
                    const locationNames =
                      option.locations
                        ?.map((loc) => loc?.name)
                        .filter((name): name is string => Boolean(name)) || [];
                    return (
                      <li {...props} key={option.id}>
                        <Stack spacing={0.5}>
                          <Typography variant="body1">{option.name}</Typography>
                          {locationNames.length > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              Attached to: {locationNames.join(", ")}
                            </Typography>
                          )}
                        </Stack>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select conversion"
                      variant="outlined"
                      className={classes.formControl}
                    />
                  )}
                />
                {!hasAttachOptions && conversionsFetched && (
                  <FormHelperText>
                    All project conversions are already attached to this
                    location.
                  </FormHelperText>
                )}
              </Stack>
            ) : (
              <>
                <StyledFlexBox>
                  <Box display="flex" alignItems="center">
                    <Switch
                      disabled={loading}
                      checked={useReserved}
                      onChange={() => setUseReserved(!useReserved)}
                    />
                    <Typography>Use Reserved Conversion</Typography>
                  </Box>
                  <ClickableTooltip
                    text={ReservedTipText}
                    icon={<StyledHelp />}
                  />
                  {useReserved && (
                    <Box my={1} sx={{ width: "100%" }}>
                      <ReservedConversionSelector
                        value={state.reserved}
                        onChange={handleSelectChange}
                        disabled={loading}
                        className={classes.formControl}
                      />
                    </Box>
                  )}
                </StyledFlexBox>

                <TextValidator
                  label="name of conversion"
                  value={state.name}
                  disabled={loading}
                  variant="outlined"
                  name="name"
                  fullWidth
                  validators={[
                    "required",
                    "minStringLength:3",
                    "maxStringLength:200",
                  ]}
                  errorMessages={[
                    "Name is required",
                    "Name must be 3 or more characters long",
                    "Name must be less than 200 characters long",
                  ]}
                  onChange={handleChange("name")}
                />

                <StyledFlexBox>
                  <Box display="flex" alignItems="center">
                    <Typography>Interaction</Typography>
                    <Switch
                      disabled={loading}
                      checked={type === ApiConversionType.MACRO}
                      onChange={handleTypeChange}
                    />
                    <Typography>Conversion</Typography>
                  </Box>
                  <ClickableTooltip
                    text={MicroMacroToolTipText}
                    icon={<StyledHelp />}
                  />
                </StyledFlexBox>
              </>
            )}
          </Stack>
        </AccordianChartBuilderSidebarBlock>

        {!useExistingConversion && (
          <AccordianChartBuilderSidebarBlock
            title="Push Events"
            subTitle="Configure third-party event tracking"
          >
            <Stack direction={"row"} justifyContent="space-between">
              <Image
                height={40}
                src={Meta_Ads_logo}
                duration={50}
                fit={"contain"}
                sx={{ objectPosition: "left" }}
              />

              <FormControlLabel
                style={{ minWidth: 136, marginRight: 0 }}
                control={
                  <Switch
                    checked={state.pushToMeta}
                    onChange={handleChange("pushToMeta")}
                  />
                }
                label="Meta Pixel"
              />
            </Stack>
          </AccordianChartBuilderSidebarBlock>
        )}

        {shouldShowTriggers && !selectedTag ? (
          <AccordianChartBuilderSidebarBlock
            title="Triggers"
            subTitle="Define user actions that create the conversion"
          >
            <TriggerList
              title="add new trigger"
              tags={[...triggers, ...newTriggers]}
              onAdd={() => setSelectedTag("new")}
              onEdit={(conversion) => {
                setSelectedTag(conversion);
              }}
              onDelete={handleTriggerDelete}
            />
          </AccordianChartBuilderSidebarBlock>
        ) : shouldShowTriggers ? (
          <>
            {/* trigger form */}
            <AccordianChartBuilderSidebarBlock
              title="New Trigger"
              subTitle="Configure the trigger type and settings"
            >
              <Stack spacing={2}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel htmlFor="outlined-age-native-simple">
                    Select trigger type
                  </InputLabel>
                  <Select
                    value={trigger}
                    onChange={(e) => handleTriggerTypeChange(e)}
                    label="Select trigger type"
                    name="trigger"
                  >
                    <MenuItem value={ApiConversionStateTrigger.CLICK}>
                      click
                    </MenuItem>
                    <MenuItem value={ApiConversionStateTrigger.SUBMIT}>
                      submit
                    </MenuItem>
                    <MenuItem value={ApiConversionStateTrigger.MANUAL}>
                      manual
                    </MenuItem>
                    <MenuItem value={ApiConversionStateTrigger.PAGE_VIEW}>
                      time active on page
                    </MenuItem>
                    <MenuItem
                      value={ApiConversionStateTrigger.ONSITE_PAGEVIEWS}
                    >
                      onsite session pageviews
                    </MenuItem>
                    <MenuItem value={ApiConversionStateTrigger.TIME_ON_ELEMENT}>
                      time element in view
                    </MenuItem>
                  </Select>
                </FormControl>
                {(trigger === ApiConversionStateTrigger.CLICK ||
                  trigger === ApiConversionStateTrigger.TIME_ON_ELEMENT ||
                  trigger === ApiConversionStateTrigger.SUBMIT) && (
                  <>
                    {trigger === ApiConversionStateTrigger.CLICK && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          How to target
                        </Typography>
                        <RadioGroup
                          row
                          value={useText ? "text" : "selector"}
                          onChange={(e) =>
                            toggleTriggerText(e.target.value === "text")
                          }
                        >
                          <FormControlLabel
                            value="selector"
                            control={<Radio />}
                            label="By element selector"
                          />
                          <FormControlLabel
                            value="text"
                            control={<Radio />}
                            label="By element text"
                          />
                        </RadioGroup>
                      </Box>
                    )}

                    {trigger === ApiConversionStateTrigger.CLICK && useText ? (
                      <>
                        <Box paddingTop={3}>
                          <TextFieldHeading variant="body1" color="textPrimary">
                            {state.lookup ===
                            ApiConversionStateLookupType.EQUALS
                              ? "Equals"
                              : state.lookup ===
                                  ApiConversionStateLookupType.CONTAINS
                                ? "Containing"
                                : "REGEX"}
                          </TextFieldHeading>
                        </Box>
                        <Box mb={3}>
                          <Typography variant="caption" color="textSecondary">
                            {state.lookup ===
                            ApiConversionStateLookupType.EQUALS
                              ? "please type the text the element should equal, (case sensitive)"
                              : state.lookup ===
                                  ApiConversionStateLookupType.CONTAINS
                                ? "please type the text the element should contain, (case insensitive)"
                                : "please type a regex expression element should contain, (case insensitive)"}
                          </Typography>
                        </Box>
                        <Stack spacing={2}>
                          <SelectValidator
                            variant="outlined"
                            fullWidth
                            label="Lookup Type"
                            name="lookup"
                            value={
                              state.lookup ||
                              ApiConversionStateLookupType.EQUALS
                            }
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                              v,
                            ) => {
                              setState((old) =>
                                produce(old, (draft) => {
                                  draft.lookup = e.target
                                    .value as ApiConversionStateLookupType;
                                }),
                              );
                            }}
                          >
                            {enumToArray(ApiConversionStateLookupType).map(
                              (t) => (
                                <MenuItem key={t} value={t}>
                                  {formatEnumVal(
                                    `${t}`.replace("CONVERSION_STATE", ""),
                                  )}
                                </MenuItem>
                              ),
                            )}
                          </SelectValidator>
                          <TextValidator
                            value={state.targetText}
                            variant="outlined"
                            name="targetText"
                            fullWidth
                            disabled={loading}
                            validators={["required"]}
                            errorMessages={["Target Text is required"]}
                            onChange={handleChange("targetText")}
                            label={
                              state.lookup ===
                              ApiConversionStateLookupType.EQUALS
                                ? "Equals"
                                : state.lookup ===
                                    ApiConversionStateLookupType.CONTAINS
                                  ? "Containing"
                                  : "REGEX"
                            }
                          />
                        </Stack>
                      </>
                    ) : (
                      <>
                        <Box paddingTop={3}>
                          <Typography variant="subtitle2" color="textPrimary">
                            Selector
                          </Typography>
                        </Box>

                        <Box paddingBottom={1}>
                          <Typography variant="caption" color="textSecondary">
                            please select item on page preview
                          </Typography>
                        </Box>
                        <TextValidator
                          value={state.selector}
                          variant="outlined"
                          name="selector"
                          fullWidth
                          disabled={loading}
                          validators={["required", "maxStringLength:512"]}
                          errorMessages={[
                            "selector is required",
                            "Selector must be less than 512 characters",
                          ]}
                          onChange={handleChange("selector")}
                        />
                        {trigger === ApiConversionStateTrigger.CLICK && (
                          <>
                            <Box mt={2}>
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Selector options
                              </Typography>
                              <Grid
                                component="label"
                                container
                                alignItems="center"
                                spacing={1}
                              >
                                <Grid item>unique</Grid>
                                <Grid item>
                                  <Switch
                                    disabled={loading}
                                    checked={
                                      selectorRepeated
                                        ? selectorRepeated
                                        : false
                                    }
                                    onChange={() =>
                                      setSelectorRepeated(!selectorRepeated)
                                    }
                                  />
                                </Grid>
                                <Grid item>repeated</Grid>
                              </Grid>
                            </Box>
                            {selectorRepeated && (
                              <Typography variant="body2">
                                You may need to refine the selector manually to
                                provide context for the repeated selector
                              </Typography>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

                {trigger === ApiConversionStateTrigger.PAGE_VIEW && (
                  <>
                    <Box paddingTop={3}>
                      <TextFieldHeading variant="body1" color="textPrimary">
                        specify time
                      </TextFieldHeading>
                    </Box>
                    <Box paddingBottom={1}>
                      <Typography variant="caption" color="textSecondary">
                        in seconds -e.g type 120 for 120 seconds
                      </Typography>
                    </Box>
                    <TextValidator
                      value={state.value}
                      variant="outlined"
                      name="value"
                      type="number"
                      fullWidth
                      disabled={loading}
                      validators={["required"]}
                      errorMessages={["value is required"]}
                      onChange={handleChange("value")}
                    />
                  </>
                )}
                {trigger === ApiConversionStateTrigger.MANUAL && (
                  <>
                    <Box paddingTop={3}>
                      <TextFieldHeading variant="body1" color="textPrimary">
                        manual conversion key
                      </TextFieldHeading>
                    </Box>
                    <Box paddingBottom={1}>
                      <Typography variant="caption" color="textSecondary">
                        assign a key to this conversion
                      </Typography>
                    </Box>
                    <TextValidator
                      value={state.selector}
                      variant="outlined"
                      name="selector"
                      fullWidth
                      disabled={loading}
                      validators={["required"]}
                      errorMessages={["selector is required"]}
                      onChange={handleChange("selector")}
                    />
                  </>
                )}
                {trigger === ApiConversionStateTrigger.ONSITE_PAGEVIEWS && (
                  <>
                    <Box paddingTop={3}>
                      <TextFieldHeading variant="body1" color="textPrimary">
                        Onsite pages
                      </TextFieldHeading>
                    </Box>
                    <Box paddingBottom={1}>
                      <Typography variant="caption" color="textSecondary">
                        list of page urls visited in 1 sessions
                        <br />
                        note that a sessions is a leads pageviews that are
                        within 30 ins apart from the last one
                        <br />
                        do not include domain here, to use contains wrap string
                        in % % e.g `%my-part-of-path%`
                      </Typography>
                    </Box>
                    <TriggerCommaValue
                      loading={loading}
                      initialValue={state.selector}
                      handleChange={handleChange("selector")}
                    />
                  </>
                )}
                {trigger === ApiConversionStateTrigger.TIME_ON_ELEMENT && (
                  <>
                    <Box paddingTop={3}>
                      <TextFieldHeading variant="body1" color="textPrimary">
                        Time Element in View
                      </TextFieldHeading>
                    </Box>

                    <Box paddingBottom={1}>
                      <Typography variant="caption" color="textSecondary">
                        in seconds -e.g type 120 for 120 seconds
                      </Typography>
                    </Box>

                    <TextValidator
                      value={state.value}
                      variant="outlined"
                      name="value"
                      type="number"
                      fullWidth
                      disabled={loading}
                      validators={["required"]}
                      errorMessages={["value is required"]}
                      onChange={handleChange("value")}
                    />
                  </>
                )}

                <Box marginY={3} display="flex" justifyContent="space-between">
                  <StyledButton
                    disableElevation
                    size="large"
                    variant="contained"
                    type="button"
                    onClick={handleSubmitTrigger}
                    fullWidth
                    color="primary"
                    disabled={
                      trigger === ApiConversionStateTrigger.PAGE_VIEW
                        ? !state.value
                        : trigger === ApiConversionStateTrigger.TIME_ON_ELEMENT
                          ? !state.selector || !state.value
                          : state.selector || state.targetText
                            ? false
                            : true
                    }
                  >
                    {loading ? (
                      <CircularProgress size={"1rem"} color="inherit" />
                    ) : (
                      "save trigger"
                    )}
                  </StyledButton>
                  <StyledButton
                    disableElevation
                    size="large"
                    fullWidth
                    variant="contained"
                    onClick={() => setSelectedTag(null)}
                    color="secondary"
                  >
                    cancel
                  </StyledButton>
                </Box>
              </Stack>
            </AccordianChartBuilderSidebarBlock>

            {selectedTag &&
              (trigger === ApiConversionStateTrigger.CLICK ||
                trigger === ApiConversionStateTrigger.TIME_ON_ELEMENT ||
                trigger === ApiConversionStateTrigger.SUBMIT) && (
                <AccordianChartBuilderSidebarBlock
                  title="Advanced Settings"
                  subTitle="Configure offline tracking and form behavior"
                  defaultClosed={true}
                >
                  <Stack spacing={2}>
                    {trigger !== ApiConversionStateTrigger.TIME_ON_ELEMENT && (
                      <StyledFlexBox marginTop={0} marginBottom={0}>
                        <Box display="flex" alignItems="center">
                          <Switch
                            disabled={loading}
                            checked={trackOffline ? trackOffline : false}
                            onChange={handleTrackChange}
                          />
                          <Typography>track offline</Typography>
                        </Box>
                        <ClickableTooltip
                          text="switching this on will enable you to track the conversion offline, by assigning a key to this trigger. You can later use the key to close the conversion and assign revenue."
                          icon={<StyledHelp />}
                        />
                      </StyledFlexBox>
                    )}
                    {trackOffline && (
                      <Stack direction={"row"} spacing={2} alignItems="center">
                        <Box sx={{ width: "100%" }}>
                          <TextValidator
                            label="offline field name"
                            value={offlineFieldName || ""}
                            disabled={loading}
                            variant="outlined"
                            name="offlinefield"
                            fullWidth
                            validators={["required"]}
                            errorMessages={["name is required"]}
                            onChange={handleOfflineFieldNameChange}
                          />
                        </Box>
                        <ClickableTooltip
                          text="This is the name of the field that we will attach to your page or form, which gets submitted with the form data and contains the unique conversion key. you can use this later to add data such as closed offline sale revenue, to this specific conversion."
                          icon={<StyledHelp />}
                        />
                      </Stack>
                    )}
                    {trigger === ApiConversionStateTrigger.SUBMIT && (
                      <StyledFlexBox mt={0}>
                        <Box display="flex" alignItems="center">
                          <Switch
                            disabled={loading}
                            checked={preventSubmit}
                            onChange={(e, checked) => setPreventSubmit(checked)}
                          />
                          <Typography
                            color={preventSubmit ? "primary" : "secondary"}
                          >
                            prevent submit
                          </Typography>
                        </Box>
                        <ClickableTooltip
                          text={preventSubmitTooltipText}
                          icon={<StyledHelp />}
                        />
                      </StyledFlexBox>
                    )}
                  </Stack>
                </AccordianChartBuilderSidebarBlock>
              )}
          </>
        ) : null}
        {!selectedTag && (
          <Box marginY={3} display="flex" justifyContent="space-between" px={3}>
            <StyledButton
              disableElevation
              size="large"
              variant="contained"
              type="submit"
              fullWidth
              color="primary"
              disabled={
                loading || (useExistingConversion && !existingConversionId)
              }
            >
              {loading ? (
                <CircularProgress size={"1rem"} color="inherit" />
              ) : (
                "save"
              )}
            </StyledButton>
            <StyledButton
              disableElevation
              size="large"
              fullWidth
              variant="contained"
              onClick={onCancel}
              color="secondary"
            >
              cancel
            </StyledButton>
          </Box>
        )}
      </ValidatorForm>
    </ChartBuilderSidebarContainer>
  );
};
